import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { Card, Badge, Spinner, Button, Input, Modal } from '../../components/ui';
import { 
  DollarSign, Search, RefreshCw, Plus, Building,
  AlertCircle, TrendingUp, Wallet, Users, CreditCard
} from 'lucide-react';
import './Admin.css';

const AdminCredits = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { request } = useApi();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const data = await request('get', '/api/admin/organizations').catch(() => []);
      setOrganizations(data || []);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!creditsAmount || parseInt(creditsAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      await request('post', `/api/admin/organizations/${selectedOrg.id}/credits/add`, {
        amount: parseInt(creditsAmount)
      });
      setShowCreditsModal(false);
      setCreditsAmount('');
      fetchOrganizations();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add credits');
    } finally {
      setSubmitting(false);
    }
  };

  const openCreditsModal = (org) => {
    setSelectedOrg(org);
    setCreditsAmount('');
    setShowCreditsModal(true);
  };

  const filteredOrgs = organizations.filter(org => 
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="admin-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  const totalCredits = organizations.reduce((sum, o) => sum + (o.creditBalance || 0), 0);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Credits Management</h1>
          <p className="admin-subtitle">Manage credits for all organizations</p>
        </div>
        <button className="refresh-btn" onClick={fetchOrganizations}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Summary */}
      <div className="admin-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '24px' }}>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #F59E0B' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{totalCredits.toLocaleString()}</div>
            <div className="admin-stat-label">Total Credits in System</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #10B981' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{organizations.length}</div>
            <div className="admin-stat-label">Organizations with Credits</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #3B82F6' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">
              {Math.round(totalCredits / (organizations.length || 1)).toLocaleString()}
            </div>
            <div className="admin-stat-label">Average Credits/Org</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="admin-filters">
        <div className="search-wrapper" style={{ maxWidth: '100%' }}>
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Organizations Table */}
      <Card className="admin-table-container" padding="none">
        <div className="table-header">
          <h3 className="table-title">Organization Credits</h3>
          <div className="table-info">{filteredOrgs.length} organizations</div>
        </div>
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Email</th>
                <th>Credit Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan={5}>
                    <div className="empty-state">
                      <DollarSign size={48} className="empty-icon" />
                      <h3>No organizations found</h3>
                      <p>Try adjusting your search</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredOrgs.map((org) => (
                  <tr key={org.id}>
                    <td>
                      <div className="org-name">{org.name}</div>
                    </td>
                    <td className="org-email">{org.email}</td>
                    <td>
                      <span className={`credit-balance ${(org.creditBalance || 0) < 100 ? 'low' : ''}`}>
                        {org.creditBalance?.toLocaleString() || 0}
                      </span>
                    </td>
                    <td>
                      <Badge variant={org.status === 'ACTIVE' ? 'success' : 'warning'}>
                        {org.status || 'Unknown'}
                      </Badge>
                    </td>
                    <td>
                      <button 
                        className="action-btn credits"
                        onClick={() => openCreditsModal(org)}
                      >
                        <Plus size={14} /> Add Credits
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Credits Modal */}
      <Modal isOpen={showCreditsModal} onClose={() => setShowCreditsModal(false)} title="Add Credits">
        <div className="credits-modal">
          <div className="credits-org-info">
            <div className="org-avatar">
              <Building size={20} />
            </div>
            <div>
              <div className="org-name">{selectedOrg?.name}</div>
              <div className="org-email">{selectedOrg?.email}</div>
            </div>
          </div>
          <div className="credits-input-wrapper">
            <label className="credits-label">Credits Amount</label>
            <div className="credits-input-group">
              <DollarSign size={18} className="credits-input-icon" />
              <input
                type="number"
                className="credits-input"
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
              />
            </div>
            <p className="credits-hint">1 credit = 1 WhatsApp message</p>
          </div>
          <div className="credits-warning">
            <AlertCircle size={14} />
            <span>Credits will be added to the organization's balance immediately.</span>
          </div>
          <div className="modal-actions">
            <Button type="button" variant="ghost" onClick={() => setShowCreditsModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCredits} loading={submitting}>
              Add Credits
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
export default AdminCredits;